/**
 * Ids the guided tours fabricate for their practice records, and the predicate
 * that keeps them off the network.
 *
 * A practice record exists only in the browser, so any request addressed to
 * one is a leak by definition: at best a 404, at worst a write against a real
 * system keyed by an id someone later mints for real. The shared RTK base query
 * refuses such requests whether or not a tour is running (and, while one runs,
 * bodies carrying one), and the few bare `fetch` sites call
 * `assertNotPracticeRequest` for the same reason.
 *
 * Isomorphic and dependency-free so both the transport and tests can import it.
 *
 * Only ids that are synthetic by construction belong here: a value inside a
 * range real records use (a gap like `J2214`, an `SVO88121`) or a real vendor
 * number would refuse real requests, so none of those is ever reserved.
 */

/** Real work-order tasks were ~WOT0032842 in Oct 2026; this is far above. */
export const PRACTICE_TASK_NUMBER = 'WOT0099999'
/** The break-fix practice invoice's task. */
export const PRACTICE_BREAKFIX_TASK_NUMBER = 'WOT0099998'
export const PRACTICE_TASK_SYS_ID = 'tour-task'
export const PRACTICE_WORK_ORDER_NUMBER = 'WO0099999'
export const PRACTICE_BREAKFIX_WORK_ORDER_NUMBER = 'WO0099998'
export const PRACTICE_WORK_ORDER_SYS_ID = 'tour-work-order'
/** Real Atlas invoices count up from TI-000001. */
export const PRACTICE_INVOICE_NUMBER = 'TI-099999'
export const PRACTICE_INVOICE_ID = 'tour-invoice'
/** Real BC jobs topped out near J10874 in Oct 2026. */
export const PRACTICE_JOB_NUMBER = 'J99999'
/** Real customer numbers start at S0022. */
export const PRACTICE_CUSTOMER_NUMBER = 'S0000'
/** Real BC service orders were ~SVO286000 in Oct 2026. */
export const PRACTICE_SVO_NUMBER = 'SVO999999'
/**
 * Real cases were ~CS0439000 in Oct 2026, ~180k a year. The top of the
 * seven-digit range is decades off; anything nearer is a few years at most.
 */
export const PRACTICE_CASE_NUMBER = 'CS9999999'

const RESERVED_PRACTICE_IDS = [
  PRACTICE_CUSTOMER_NUMBER,
  PRACTICE_CASE_NUMBER,
  PRACTICE_TASK_NUMBER,
  PRACTICE_BREAKFIX_TASK_NUMBER,
  PRACTICE_TASK_SYS_ID,
  PRACTICE_WORK_ORDER_NUMBER,
  PRACTICE_BREAKFIX_WORK_ORDER_NUMBER,
  PRACTICE_WORK_ORDER_SYS_ID,
  PRACTICE_INVOICE_NUMBER,
  PRACTICE_INVOICE_ID,
  PRACTICE_JOB_NUMBER,
  PRACTICE_SVO_NUMBER
] as const

/** One of the fixed practice ids a tour can own. */
export type PracticeId = (typeof RESERVED_PRACTICE_IDS)[number]

const RESERVED = new Set<string>(RESERVED_PRACTICE_IDS)

/**
 * Ids the tours mint at runtime (`tour-photo-3`, `tour-cost-<lineId>`, …).
 * Anchored, so the whole value must match: `my-tour-photo-1` is not one.
 */
const RESERVED_FAMILIES: readonly RegExp[] = [
  /^tour-photo-\d+$/,
  /^tour-note-\d+$/,
  /^tour-cost-.+$/,
  /^tour-receipt-.+$/,
  // The practice case form's contacts, assignment groups and assignees: sys_ids
  // a ServiceNow write would carry in its body.
  /^tour-contact-\d+$/,
  /^tour-group-\d+$/,
  /^tour-agent-\d+$/
]

/**
 * Whether `value` is, exactly, a practice id. Exact on purpose: `WOT00999990`
 * and `TI-000118` are real-shaped ids that must pass.
 */
export function isReservedPracticeId(value: unknown): boolean {
  if (typeof value !== 'string' || value === '') return false
  return RESERVED.has(value) || RESERVED_FAMILIES.some((re) => re.test(value))
}

// Deep enough for any request body the app builds; the bound is what makes a
// pathological or cyclic body cheap to refuse to walk rather than a hang.
const MAX_BODY_DEPTH = 12

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function findInUrl(url: string): string | null {
  // A base is required for the relative paths RTK builds ('/technician/…');
  // its host is never read.
  let parsed: URL
  try {
    parsed = new URL(url, 'http://practice-guard.invalid')
  } catch {
    return null
  }
  for (const segment of parsed.pathname.split('/')) {
    const decoded = safeDecode(segment)
    if (isReservedPracticeId(decoded)) return decoded
  }
  for (const value of parsed.searchParams.values()) {
    if (isReservedPracticeId(value)) return value
  }
  return null
}

function findInValue(value: unknown, depth: number): string | null {
  if (typeof value === 'string')
    return isReservedPracticeId(value) ? value : null
  if (value === null || typeof value !== 'object' || depth > MAX_BODY_DEPTH) {
    return null
  }
  if (value instanceof ArrayBuffer) return null
  if (typeof Blob !== 'undefined' && value instanceof Blob) return null
  if (value instanceof URLSearchParams) {
    return findInValue([...value.values()], depth + 1)
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    // File entries are skipped by the string check above.
    return findInValue([...value.values()], depth + 1)
  }
  const children = Array.isArray(value) ? value : Object.values(value)
  for (const child of children) {
    const found = findInValue(child, depth + 1)
    if (found) return found
  }
  return null
}

/**
 * The practice id a request ADDRESSES — a URL path segment, a query value, or
 * a query param — or null. Checked on every request: an address naming a
 * practice record is a leak whether or not a tour is running.
 */
export function urlMentionsPracticeId(
  url: string,
  params?: unknown
): string | null {
  return findInUrl(url) ?? findInValue(params, 0)
}

/**
 * The practice id a request body carries in a JSON leaf, or null. Only worth
 * asking while a tour runs — practice writes only happen then — because a body
 * is mostly free text, and a note or search that happens to read "J99999"
 * outside a tour is not a practice record.
 */
export function bodyMentionsPracticeId(body: unknown): string | null {
  // RTK sends JSON as a string sometimes; the leaves inside it name a record.
  if (typeof body === 'string') {
    if (isReservedPracticeId(body)) return body
    try {
      return findInValue(JSON.parse(body), 0)
    } catch {
      return null
    }
  }
  return findInValue(body, 0)
}

/** `data.code` on an RTK request the practice guard refused. */
export const PRACTICE_BLOCKED_CODE = 'PRACTICE_BLOCKED'

/**
 * What a viewer is told when a real write is refused because a tour that
 * intercepts it is running: they pressed a real button, and nothing happened.
 */
export const PRACTICE_INTERCEPTED_MESSAGE =
  "You're in a tour — nothing is saved."

/** Whether an RTK error is the practice guard's refusal, not a failure. */
export function isPracticeBlockedError(error: unknown): boolean {
  const data = (error as { data?: { code?: unknown } } | null)?.data
  return data?.code === PRACTICE_BLOCKED_CODE
}

/** Thrown by `assertNotPracticeRequest`; tell it apart with `instanceof`. */
export class PracticeRequestBlockedError extends Error {
  constructor(readonly practiceId: string) {
    super('A practice record never leaves the browser.')
    this.name = 'PracticeRequestBlockedError'
  }
}

/**
 * The RTK guard's address check, for code that calls `fetch` directly. Throws
 * rather than returning so a forgotten check fails loudly instead of sending.
 * URL only: a bare fetch site cannot tell whether a tour is running, which is
 * what a body check depends on (see bodyMentionsPracticeId).
 */
export function assertNotPracticeRequest(url: string): void {
  const practiceId = urlMentionsPracticeId(url)
  if (!practiceId) return
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[tours] refused a request naming practice id ${practiceId}`)
  }
  throw new PracticeRequestBlockedError(practiceId)
}
